import { useState, useEffect, useRef, useMemo } from 'react';
import { transformMarketData } from '../utils/oddsHelpers';
import { DEFAULT_MARKET, isGameFinished, getDefaultMarketForSport } from '../utils/constants';

export const useOddsData = (games, sport, isAuthenticated = true) => {
  const [playersByGameId, setPlayersByGameId] = useState({});
  const [selectedMarkets, setSelectedMarkets] = useState({}); // Track selected market per game
  const [loadingGameIds, setLoadingGameIds] = useState(new Set()); // Track which games are loading
  const [initialLoadComplete, setInitialLoadComplete] = useState(false); // Track if initial 3s delay is done
  const [debugStats, setDebugStats] = useState({
    totalGames: 0,
    completedGames: 0,
    loadingGames: 0,
    queuedGames: 0,
    activeRequests: 0,
    errors: 0,
    requestTimes: [],
    avgRequestTime: 0
  });
  const prevMarketsRef = useRef({}); // Track previous markets to detect changes
  const prevGamesRef = useRef([]); // Track previous games to detect sport changes
  const prevSportRef = useRef(sport); // Track previous sport
  const initialLoadTimerRef = useRef(null); // Track the initial load timer
  const hasStartedInitialTimerRef = useRef(false); // Track if we've started the timer at all

  const handleMarketSelection = (gameId, marketKey) => {
    setSelectedMarkets(prev => ({
      ...prev,
      [gameId]: marketKey
    }));
  };

  const getSelectedMarketForGame = (gameId) => {
    return selectedMarkets[gameId] || getDefaultMarketForSport(sport);
  };

  // Memoize games to prevent unnecessary re-renders
  const memoizedGames = useMemo(() => games, [games.length, games.map(g => g.gameIDmlb).join(',')]);

  useEffect(() => {
    // If not authenticated, skip API calls but still start the timer
    // so the UI isn't stuck in loading state forever
    if (!isAuthenticated) {
      // Start the timer even when not authenticated so initialLoadComplete eventually becomes true
      if (!hasStartedInitialTimerRef.current) {
        hasStartedInitialTimerRef.current = true;
        initialLoadTimerRef.current = setTimeout(() => {
          setInitialLoadComplete(true);
        }, 1000);
      }
      return; // Skip all API calls
    }

    // Check if sport has changed
    if (sport !== prevSportRef.current) {
      console.log('Sport changed from', prevSportRef.current, 'to', sport, '- clearing data');
      // Clear all data when sport changes
      setPlayersByGameId({});
      setSelectedMarkets({});
      setLoadingGameIds(new Set());
      setInitialLoadComplete(false); // Reset initial load on sport change
      prevMarketsRef.current = {}; // Clear previous markets
      prevSportRef.current = sport;
      prevGamesRef.current = [];  // Reset to empty so games change isn't detected on next run

      // Clear any existing timer and reset flag, then start new timer
      if (initialLoadTimerRef.current) {
        clearTimeout(initialLoadTimerRef.current);
        initialLoadTimerRef.current = null;
      }
      hasStartedInitialTimerRef.current = false; // Reset the flag

      // Start the timer immediately for the new sport
      hasStartedInitialTimerRef.current = true;
      initialLoadTimerRef.current = setTimeout(() => {
        setInitialLoadComplete(true);
      }, 1000);

      return; // Exit early to prevent processing old games
    }

    // Set initial load timer (1 second) - only on first load or sport change
    if (!hasStartedInitialTimerRef.current) {
      hasStartedInitialTimerRef.current = true;
      initialLoadTimerRef.current = setTimeout(() => {
        setInitialLoadComplete(true);
      }, 1000);
    }
    
    // Check if this is a new set of games - compare game IDs
    const currentGameIds = games.map(g => g.gameIDmlb).sort().join(',');
    const prevGameIds = prevGamesRef.current.map(g => g.gameIDmlb || '').sort().join(',');
    
    if (currentGameIds !== prevGameIds && prevGamesRef.current.length > 0) {
      // Clear data when games change
      setPlayersByGameId({});
      setSelectedMarkets({});
      setLoadingGameIds(new Set());
      setInitialLoadComplete(false); // Reset initial load when games change

      // Clear any existing timer and reset flag
      if (initialLoadTimerRef.current) {
        clearTimeout(initialLoadTimerRef.current);
        initialLoadTimerRef.current = null;
      }
      hasStartedInitialTimerRef.current = false; // Reset the flag
    }
    
    // Update previous games reference
    prevGamesRef.current = games;
    
    // Create a request queue to limit concurrent requests
    const requestQueue = [];
    let activeRequests = 0;
    const MAX_CONCURRENT_REQUESTS = 5;
    
    // Create abort controller for this batch of requests
    const abortController = new AbortController();
    const { signal } = abortController;
    
    const processQueue = async () => {
      while (requestQueue.length > 0 && activeRequests < MAX_CONCURRENT_REQUESTS) {
        const request = requestQueue.shift();
        activeRequests++;
        
        // Update debug stats
        setDebugStats(prev => ({
          ...prev,
          queuedGames: requestQueue.length,
          activeRequests: activeRequests
        }));
        
        try {
          await request();
        } finally {
          activeRequests--;
          setDebugStats(prev => ({
            ...prev,
            queuedGames: requestQueue.length,
            activeRequests: activeRequests
          }));
          processQueue(); // Process next request in queue
        }
      }
    };
    
    const queueRequest = (fn) => {
      requestQueue.push(fn);
      processQueue();
    };
    
    const fetchData = async (gameId, isMarketChange = false) => {
      // Skip fetching odds for locked games
      if (gameId?.startsWith('locked_')) {
        console.log(`Skipping odds fetch for locked game: ${gameId}`);
        return;
      }

      // Set a minimum display time for skeleton to prevent flashing
      const minimumLoadTime = 200; // milliseconds
      const startTime = Date.now();

      try {
        // Only set loading state for market changes for UI purposes (skeleton display)
        if (isMarketChange) {
          setLoadingGameIds(prev => new Set(prev).add(gameId));
        }
        
        // Update debug stats when starting request
        setDebugStats(prev => ({
          ...prev,
          loadingGames: prev.loadingGames + 1
        }));
        
        // Get the selected market for this game
        const selectedMarket = getSelectedMarketForGame(gameId);

        // Determine the actual market to use in the endpoint
        // For all h2h-related markets, spreads, and totals, always fetch ?all=true to get all periods
        let marketEndpoint = selectedMarket;
        let queryParams = '';

        // Always use h2h endpoint with ?all=true for any h2h market, spreads, or totals
        // h2h_3_way is used for soccer 3-way moneyline (with draw option)
        if (selectedMarket === 'h2h' || selectedMarket === 'h2h_3_way' || selectedMarket === 'h2h_quarters' || selectedMarket === 'h2h_halves' || selectedMarket === 'h2h_all' || selectedMarket === 'spreads' || selectedMarket === 'totals') {
          marketEndpoint = 'h2h';
          queryParams = '?all=true';
        }

        // Fetch data for the selected market
        const response = await fetch(`/api/games/${sport}/${gameId}/odds/${marketEndpoint}${queryParams}`, { signal });
        const marketData = await response.json();

        // Debug Curry in raw API response
        if (sport === 'nba' && selectedMarket === 'player_points') {
          const curryPlayer = marketData.players?.find(p => p.playerName === 'Stephen Curry');
          if (curryPlayer) {
            const point445Market = curryPlayer.markets?.find(m => m.point === 44.5);
            if (point445Market) {
              console.log('Curry 44.5 RAW API response:', {
                market: point445Market.market,
                point: point445Market.point,
                books: point445Market.books,
                dkOver: point445Market.books?.draftkings?.over
              });
            }
          }
        }

        // Calculate how long the request took
        const elapsedTime = Date.now() - startTime;
        
        // If request was very fast, wait a bit to prevent skeleton flash
        if (elapsedTime < minimumLoadTime) {
          await new Promise(resolve => 
            setTimeout(resolve, minimumLoadTime - elapsedTime)
          );
        }
        
        // NCAAB, NBA, WNBA, NFL, NHL, NCAAF, Soccer endpoints return data in 'markets' array instead of 'players'
        // Normalize to use 'players' key for consistency
        if ((sport === 'mlb' || sport === 'ncaab' || sport === 'nba' || sport === 'wnba' || sport === 'nfl' || sport === 'nhl' || sport === 'ncaaf' || sport === 'soccer') && marketData.markets && !marketData.players) {
          marketData.players = marketData.markets;
        }

        // Special handling for h2h/mainline markets - data structure is different
        // Structure: { players: [{ h2h: [...], spreads: [...], totals: [...] }] }
        // This data should pass through without transformation
        const isMainlineMarket = selectedMarket === 'h2h' || selectedMarket === 'h2h_3_way' ||
                                 selectedMarket === 'h2h_quarters' || selectedMarket === 'h2h_halves' ||
                                 selectedMarket === 'h2h_all' || selectedMarket === 'spreads' || selectedMarket === 'totals';

        if (isMainlineMarket && marketData.players?.length > 0) {
          const firstContainer = marketData.players[0];
          // Check if this is the mainline structure (has h2h, spreads, or totals arrays)
          if (firstContainer && (firstContainer.h2h || firstContainer.spreads || firstContainer.totals)) {
            console.log('H2H/Mainline market detected, passing through without transformation:', {
              gameId,
              selectedMarket,
              hasH2H: !!firstContainer.h2h,
              hasSpreads: !!firstContainer.spreads,
              hasTotals: !!firstContainer.totals
            });
            // Pass through directly - H2HDataTable expects this structure
            const newData = { players: marketData.players, last_update: marketData.lastUpdate || new Date().toISOString() };
            setPlayersByGameId((prev) => ({ ...prev, [gameId]: newData }));

            // Clear loading state
            if (isMarketChange) {
              setLoadingGameIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(gameId);
                return newSet;
              });
            }

            // Update debug stats on success
            setDebugStats(prev => {
              const requestTime = Date.now() - startTime;
              const newRequestTimes = [...prev.requestTimes, requestTime].slice(-20);
              const avgTime = newRequestTimes.reduce((sum, time) => sum + time, 0) / newRequestTimes.length;
              return {
                ...prev,
                completedGames: prev.completedGames + 1,
                loadingGames: Math.max(0, prev.loadingGames - 1),
                requestTimes: newRequestTimes,
                avgRequestTime: Math.round(avgTime)
              };
            });
            return; // Skip normal processing
          }
        }

        // Check if data is already flattened (new backend format)
        // Flattened data has players array with direct field names like "player_points_fanduel_over"
        let isAlreadyFlattened = false;
        if (marketData.players?.length > 0) {
          // Skip players with old binary format when checking
          let checkIndex = 0;
          let firstPlayer = marketData.players[checkIndex];

          // If first player has old binary format, try next players
          while (firstPlayer && firstPlayer.hasOwnProperty(`${selectedMarket}_binary`) && checkIndex < marketData.players.length - 1) {
            checkIndex++;
            firstPlayer = marketData.players[checkIndex];
          }

          // Check if ANY field matches the flattened format pattern
          // Look for fields like "player_points_BOOKNAME_over" or "player_points_BOOKNAME"
          // Also handles NCAAB format like "h2h_BOOKNAME_home" or "h2h_BOOKNAME_away"
          const flattenedPattern = new RegExp(`^${selectedMarket}_\\w+_(over|under|home|away)$|^${selectedMarket}_\\w+$`);
          isAlreadyFlattened = Object.keys(firstPlayer).some(key => flattenedPattern.test(key) && !key.includes('bestBook'));

          console.log('Flattened detection:', {
            gameId,
            selectedMarket,
            isAlreadyFlattened,
            checkIndex,
            firstPlayerName: firstPlayer.name || firstPlayer.playerName,
            hasOldBinary: firstPlayer.hasOwnProperty(`${selectedMarket}_binary`),
            sampleKeys: Object.keys(firstPlayer).filter(k => k.includes(selectedMarket) || k.includes('player_')).slice(0, 10)
          });
        }

        // Transform only if using old nested format (historical odds, etc.)
        let normalized;
        if (isAlreadyFlattened) {
          normalized = marketData.players;
        } else {
          // Old format - needs transformation
          try {
            normalized = transformMarketData(marketData, selectedMarket);
          } catch (err) {
            console.error('Error transforming market data:', {
              error: err.message,
              gameId,
              selectedMarket,
              marketDataKeys: Object.keys(marketData),
              hasPlayers: !!marketData.players,
              playersLength: marketData.players?.length
            });
            // Return empty array on transform error
            normalized = [];
          }
        }

        // Filter out players with old nested binary format (stale data)
        if (isAlreadyFlattened) {
          const beforeCount = normalized.length;
          normalized = normalized.filter(player => {
            // Skip players with old nested binary format
            const hasOldFormat = player.hasOwnProperty(`${selectedMarket}_binary`);
            if (hasOldFormat) {
              console.log('Skipping player with old binary format:', player.name || player.playerName);
            }
            return !hasOldFormat;
          });
          if (beforeCount !== normalized.length) {
            console.log(`Filtered out ${beforeCount - normalized.length} players with old format (${normalized.length} remaining)`);
          }
        }

        console.log('Normalized data:', {
          gameId,
          selectedMarket,
          count: normalized?.length,
          firstPlayer: normalized?.[0]?.name,
          totalKeys: normalized?.[0] ? Object.keys(normalized[0]).length : 0,
          oddsKeys: normalized?.[0] ? Object.keys(normalized[0]).filter(k => k.includes(selectedMarket) && k.includes('_')).slice(0, 10) : [],
          sampleMetadataKeys: normalized?.[0] ? Object.keys(normalized[0]).filter(k => !k.includes(selectedMarket)).slice(0, 10) : []
        });

        // Get last update time from the response - check multiple possible locations
        const last_update = marketData.lastUpdate || 
                           marketData.last_update || 
                           marketData.players?.[0]?.lastUpdate || 
                           marketData.players?.[0]?.last_update ||
                           marketData.metadata?.lastUpdate ||
                           marketData.metadata?.last_update ||
                           new Date().toISOString();
        
        // Enrich players with hand info from game data (awayPlayers/homePlayers)
        const game = games.find(g => (g.gameIDmlb || g.gameId || g.id) === gameId);
        if (game) {
          normalized.forEach(player => {
            if (player.bats && player.throws) return; // already has hand info
            const id = String(player.playerId || '').replace(/^mlb_/, '');
            const key = `ID${id}`;
            const gamePlayer = game.awayPlayers?.[key] || game.homePlayers?.[key];
            if (gamePlayer) {
              if (!player.bats && gamePlayer.batSide?.code) player.bats = gamePlayer.batSide.code;
              if (!player.throws && gamePlayer.pitchHand?.code) player.throws = gamePlayer.pitchHand.code;
            }
          });
        }

        setPlayersByGameId((prev) => {
          const prevData = prev[gameId]?.players || [];
          const updated = {};


          normalized.forEach((player, idx) => {
            const prevPlayer = prevData.find(p => p.name === player.name) || {};
            Object.keys(player).forEach((key) => {
              if (player[key] !== prevPlayer[key] && prevPlayer[key] !== undefined && prevPlayer[key] !== null && prevPlayer[key] !== '') {
                const currentValue = parseFloat(player[key]);
                const previousValue = parseFloat(prevPlayer[key]);
                if (!isNaN(currentValue) && !isNaN(previousValue) && previousValue !== 0) {
                  if (currentValue > previousValue) {
                    player[`${key}_direction`] = 'up';
                  } else if (currentValue < previousValue) {
                    player[`${key}_direction`] = 'down';
                  }
                  setTimeout(() => {
                    player[`${key}_direction`] = null;
                  }, 15000);
                }
              }
            });
            updated[idx] = player;
          });
          const newData = { players: normalized, last_update: last_update };
          return { ...prev, [gameId]: newData };
        });
        
        // Clear loading state for this game only if it was set (market change)
        if (isMarketChange) {
          setLoadingGameIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(gameId);
            return newSet;
          });
        }
        
        // Update debug stats on success
        setDebugStats(prev => {
          const requestTime = Date.now() - startTime;
          const newRequestTimes = [...prev.requestTimes, requestTime].slice(-20); // Keep last 20 times
          const avgTime = newRequestTimes.reduce((sum, time) => sum + time, 0) / newRequestTimes.length;
          
          return {
            ...prev,
            completedGames: prev.completedGames + 1,
            loadingGames: prev.loadingGames - 1,
            requestTimes: newRequestTimes,
            avgRequestTime: Math.round(avgTime)
          };
        });
        
      } catch (err) {
        // Don't log errors for aborted requests
        if (err.name !== 'AbortError') {
          console.error(`Error fetching odds for game ${gameId}:`, err);
          setPlayersByGameId((prev) => ({ ...prev, [gameId]: { players: [], last_update: null } }));
        }
        
        // Clear loading state even on error, but only if it was set (market change)
        if (isMarketChange) {
          setLoadingGameIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(gameId);
            return newSet;
          });
        }
        
        // Update debug stats on error
        setDebugStats(prev => {
          const requestTime = Date.now() - startTime;
          const newRequestTimes = [...prev.requestTimes, requestTime].slice(-20);
          const avgTime = newRequestTimes.reduce((sum, time) => sum + time, 0) / newRequestTimes.length;
          
          return {
            ...prev,
            errors: prev.errors + 1,
            loadingGames: prev.loadingGames - 1,
            requestTimes: newRequestTimes,
            avgRequestTime: Math.round(avgTime)
          };
        });
      }
    };

    // Track market changes for loading state
    const marketChanges = new Set();
    
    // Update initial debug stats
    setDebugStats(prev => ({
      ...prev,
      totalGames: memoizedGames.length,
      completedGames: 0, // Reset on new fetch cycle
      loadingGames: 0,
      queuedGames: memoizedGames.length,
      activeRequests: 0,
      errors: 0 // Reset errors on new cycle
    }));
    
    memoizedGames.forEach(game => {
      const currentMarket = getSelectedMarketForGame(game.gameIDmlb);
      const prevMarket = prevMarketsRef.current[game.gameIDmlb];
      const isMarketChange = prevMarket && prevMarket !== currentMarket;

      //console.log(`Processing game ${game.gameIDmlb} with market ${currentMarket} (sport: ${sport})`);

      if (isMarketChange) {
        marketChanges.add(game.gameIDmlb);

        // Check if switching between mainline (h2h/spreads/totals) and player prop markets
        // Data formats are incompatible, so clear the game data to force loading state
        const mainlineMarkets = ['h2h', 'h2h_quarters', 'h2h_halves', 'h2h_all', 'spreads', 'totals'];
        const wasMainline = mainlineMarkets.includes(prevMarket);
        const isMainline = mainlineMarkets.includes(currentMarket);

        if (wasMainline !== isMainline) {
          // Market type changed (mainline <-> player prop), clear data for this game
          console.log(`Market type changed for ${game.gameIDmlb}: ${prevMarket} -> ${currentMarket}, clearing data`);
          setPlayersByGameId(prev => {
            const newData = { ...prev };
            delete newData[game.gameIDmlb];
            return newData;
          });
        }
      }

      prevMarketsRef.current[game.gameIDmlb] = currentMarket;

      // Queue the request instead of calling directly
      queueRequest(() => fetchData(game.gameIDmlb, isMarketChange));
    });

    const intervals = memoizedGames
      .filter(game => !isGameFinished(game.status))
      .map((game) => {
        return setInterval(() => fetchData(game.gameIDmlb, false), 60000);
      });

    // Handle page visibility changes (screen lock/unlock)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible again, refetch all games
        console.log('Page visible again, refetching odds...');
        memoizedGames.forEach(game => {
          queueRequest(() => fetchData(game.gameIDmlb, false));
        });
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      intervals.forEach(clearInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Cancel pending requests on cleanup
      abortController.abort();
      // DON'T clear the initial load timer - let it complete even if effect re-runs
    };
  }, [memoizedGames, selectedMarkets, sport, isAuthenticated]);

  // Determine loading state: show loading if:
  // 1. Initial load hasn't completed AND no data has been fetched yet
  // 2. OR a specific game is in the loadingGameIds set (market change)
  const hasAnyData = Object.keys(playersByGameId).length > 0;
  const effectiveLoadingGameIds = (!initialLoadComplete && !hasAnyData)
    ? new Set(games.map(g => g.gameIDmlb))
    : loadingGameIds;

  return {
    playersByGameId,
    selectedMarkets,
    loadingGameIds: effectiveLoadingGameIds,
    handleMarketSelection,
    getSelectedMarketForGame,
    debugStats
  };
};